/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useState } from 'react'
import { InputNumber, message } from 'antd'
import BaseModal from 'src/components/ts-components/BaseModal'
import HeaderActionButton from 'src/components/ActionButton/HeaderActionButton'
import { balanceLabel, balanceText } from '../utils/balanceI18n'

const MIN_SLOTS = 1
const MAX_SLOTS = 60

type FirstRunModalProps = {
  open: boolean
  submitting: boolean
  onSubmit: (count: number) => Promise<unknown>
  onClose: () => void
}

/**
 * Spec flow (b): "How many players will split the team fee?" — creates N open
 * slots; the fee auto-divides penny-exact on creation.
 *
 * James, Jul 31: the modal is named for what it ASKS ("Expected Player Slots"),
 * not for the artefact it sets up, and the body no longer names the division —
 * the admin opened this from that division's sheet, so restating it was noise
 * that also forced an interpolation through a translation.
 */
const FirstRunModal = ({
  open,
  submitting,
  onSubmit,
  onClose,
}: FirstRunModalProps) => {
  const [count, setCount] = useState<number>(10)

  const clamp = (value: number): number =>
    Math.min(Math.max(Math.round(value || MIN_SLOTS), MIN_SLOTS), MAX_SLOTS)

  const submit = () => {
    onSubmit(clamp(count)).catch((error) => {
      message.error((error as Error)?.message || 'Unable to create the slots.')
    })
  }

  return (
    <BaseModal
      title={balanceLabel('common:balanceFirstRunTitle')}
      isOpen={open}
      onClose={onClose}
      desktopWidth='480px'
      destroyOnClose
      /**
       * The house action button is `HeaderActionButton`, NOT an antd Button —
       * that is what "Add Player Slot" on this tab and Registration Setup are.
       * antd's default footer renders its own Buttons, which left this modal's
       * confirm as the only primary action on the tab that did not match.
       *
       * `size='small'` + `textWeight='regular'` deliberately: that is exactly what
       * Add Player Slot beside it uses (32px, 12px label). The 40px `default`
       * size renders a 16px label, which made the footer read a full step heavier
       * than every other action on the sheet.
       */
      actions={
        <div
          css={css`
            display: flex;
            justify-content: flex-end;
            gap: 8px;
          `}
        >
          <HeaderActionButton
            variant='secondary'
            size='small'
            textWeight='regular'
            text={balanceLabel('common:cancel')}
            onClick={onClose}
            disabled={submitting}
            data-testid='first-run-cancel'
          />
          <HeaderActionButton
            variant='primary'
            size='small'
            textWeight='regular'
            text={balanceLabel('common:addLineItem')}
            onClick={submit}
            isLoading={submitting}
            data-testid='first-run-submit'
          />
        </div>
      }
    >
      <p
        css={css`
          margin-bottom: 12px;
        `}
        data-testid='first-run-body'
      >
        {balanceText('common:balanceFirstRunBody')}
      </p>
      <InputNumber
        min={MIN_SLOTS}
        max={MAX_SLOTS}
        value={count}
        onChange={(value) => setCount(clamp(Number(value ?? MIN_SLOTS)))}
        data-testid='first-run-count'
        css={css`
          width: 120px;
        `}
      />
    </BaseModal>
  )
}

export default FirstRunModal
